import { reelPrice } from './reel-storyboard.js';
/** Mock imagery: every photo/generated asset is a CSS gradient "scene" until real media exists. */
export const SCENES = {
  dusk: 'radial-gradient(circle at 70% 28%,rgba(255,214,150,.9),transparent 26%),linear-gradient(180deg,#f0b36e 0%,#d9785c 32%,#5c4b70 62%,#1e2823 100%)',
  deck: 'linear-gradient(180deg,#d6e4ec 0%,#93b5c8 44%,#3a6a86 45%,#254a63 100%)',
  living: 'linear-gradient(160deg,#f3e6d2 0%,#cfa87e 55%,#6a4a33 100%)',
  bedroom: 'linear-gradient(180deg,#f6f1e8 0%,#dccdb9 58%,#8a7866 100%)',
  kitchen: 'linear-gradient(170deg,#eff1eb 0%,#b9c3b4 58%,#4a5a4d 100%)',
  fog: 'linear-gradient(180deg,#e9edf0 0%,#b9c5cb 55%,#5d6f78 100%)',
  fire: 'radial-gradient(circle at 50% 82%,#f5a54d 0%,#8b3c1a 38%,#1a1412 72%)',
  drone: 'linear-gradient(180deg,#6b9553 0%,#3f6d3b 52%,#2c4529 100%)',
  bath: 'linear-gradient(180deg,#f4f4f1 0%,#d5d8d2 60%,#8d948b 100%)',
  trail: 'linear-gradient(180deg,#c9d7c2 0%,#7f9b6e 50%,#3c5535 100%)',
} as const;
export type SceneKey = keyof typeof SCENES;
export const SCENE_KEYS = Object.keys(SCENES) as SceneKey[];

/** Tint a scene with warm light; `warm` is 0–3. */
export function sceneBackground(scene: SceneKey, warm = 0): string {
  const base = SCENES[scene];
  if (!warm) return base;
  const a = (0.12 * warm).toFixed(2);
  return `linear-gradient(rgba(255,200,120,${a}),rgba(255,200,120,${a})),${base}`;
}

export const CHANNELS = ['IG', 'FB', 'TT', 'YT'] as const;
export type Channel = (typeof CHANNELS)[number];
export const CHANNEL_META: Record<Channel, { label: string; color: string }> = {
  IG: { label: 'Instagram', color: '#c13584' },
  FB: { label: 'Facebook', color: '#1877f2' },
  TT: { label: 'TikTok', color: '#111' },
  YT: { label: 'Shorts', color: '#e62117' },
};

export const FORMATS = ['Image', 'Carousel', 'Story', 'Reel'] as const;
export type PostFormat = (typeof FORMATS)[number];
export const FORMAT_CANVAS: Record<
  PostFormat,
  { aspect: string; height: number; headlineSize: string }
> = {
  Image: { aspect: '4/5', height: 412, headlineSize: '9cqw' },
  Carousel: { aspect: '4/5', height: 412, headlineSize: '9cqw' },
  Story: { aspect: '9/16', height: 440, headlineSize: '10cqw' },
  Reel: { aspect: '9/16', height: 440, headlineSize: '10cqw' },
};

/** How the picture is made. One row of four in the UI; maps 1:1 onto the content API. */
export const MEDIA_MODES = ['photos', 'enhance', 'reimagine', 'imagine'] as const;
export type MediaMode = (typeof MEDIA_MODES)[number];
export const MEDIA_MODE_META: Record<MediaMode, { label: string; blurb: string }> = {
  photos: { label: 'As shot', blurb: 'Your photo, cropped to the format.' },
  enhance: {
    label: 'Enhance',
    blurb: 'Your photo, improved: light, colour, sky, season, clutter gone. Same scene.',
  },
  reimagine: {
    label: 'Reimagine',
    blurb: 'A new picture that uses your photos as references, so it still looks like your place.',
  },
  imagine: {
    label: 'Imagine',
    blurb:
      'No photos. Built from the brief and what we know about the property. Concepts, offers, seasonal art.',
  },
};
/** Modes that start from a real photo (and therefore need one). */
export function modeUsesPhotos(mode: MediaMode): boolean {
  return mode !== 'imagine';
}

export const GOALS = ['aware', 'book'] as const;
export type Goal = (typeof GOALS)[number];

export const PLANS = ['starter', 'pro'] as const;
export type PlanKey = (typeof PLANS)[number];
export const BILLING_INTERVALS = ['month', 'year'] as const;
export type BillingInterval = (typeof BILLING_INTERVALS)[number];
/** New subscriptions bill annually (shown as a monthly price) unless the host picks monthly. */
export const DEFAULT_INTERVAL: BillingInterval = 'year';
/** A price lock, never a deadline: the annual price a host signs up at stays theirs. */
export const ANNUAL_LABEL = 'Founding annual price, locked for as long as you stay subscribed';
/** Preselected on signup: the plan most hosts begin on. */
export const DEFAULT_PLAN: PlanKey = 'starter';

/*
 * Pricing and metering constants. Every number here is first-pass and "not gospel" (2026-09-13):
 * changing one is one edit, and the apps and the marketing site quote these, never a typed copy.
 * Derived figures (monthly equivalent, savings, the day-7 charge) come from domain/plan.ts.
 */

/** Retail value of one credit, in cents. */
export const CREDIT_PRICE_CENTS = 50;
/** Extra credits are sold in whole multiples of this pack. */
export const CREDIT_PACK = { credits: 20, priceCents: 1000 } as const;
/**
 * What a generation spends, by what is made: a picture per card drawn. A reel has one price
 * everywhere, the trial-length reel's storyboard and filming (`reelPrice` in reel-storyboard.ts).
 */
export const CREDIT_COST = {
  /** A still from the host's photo as shot. */
  still: 1,
  /** A still the generator enhanced or reimagined from the host's photos. */
  enhancedStill: 2,
  /** A still imagined with no reference photo. */
  imaginedStill: 3,
  /** Words only; the picture is kept. */
  rewrite: 1,
  /** One music bed for a reel. */
  musicTrack: 1,
} as const;
/** The reel the trial pack promises: the default 15 s reel, filmed once. */
export const TRIAL_REEL = { durationSeconds: 15, takes: 1 } as const;
/**
 * Charged at signup and credited to the first invoice: "your first post and Reel". Its credits are
 * derived, never typed: the dearest picture plus a whole trial-length reel (storyboard and
 * filming) at the prices the API spends, so the promise holds whenever a price changes.
 */
export const TRIAL_PACK = {
  priceCents: 500,
  credits:
    Math.max(CREDIT_COST.still, CREDIT_COST.enhancedStill, CREDIT_COST.imaginedStill) +
    reelPrice(TRIAL_REEL.durationSeconds).total,
} as const;
/** How long an anonymous import is kept before it is removed. */
export const UNCLAIMED_IMPORT_DAYS = 30;

export const PLAN_META: Record<
  PlanKey,
  {
    name: string;
    /** Per property per month, billed monthly. */
    priceCents: number;
    /** Per property per year, billed annually. */
    yearlyPriceCents: number;
    summary: string;
    tag?: string;
    /** Credits granted each month; unspent grant credits expire at the next grant. */
    creditsPerMonth: number;
    channels: readonly Channel[];
  }
> = {
  starter: {
    name: 'Starter',
    priceCents: 2900,
    yearlyPriceCents: 19900,
    summary: '30 credits a month · Instagram & Facebook',
    tag: 'Most hosts start here',
    creditsPerMonth: 30,
    channels: ['IG', 'FB'],
  },
  pro: {
    name: 'Pro',
    priceCents: 5900,
    yearlyPriceCents: 44900,
    summary: '80 credits a month · all channels',
    creditsPerMonth: 80,
    channels: CHANNELS,
  },
};
export const TRIAL_DAYS = 7;
/** The marketing site serves the legal pages; the app links to them and has none of its own. */
export const LEGAL = {
  terms: 'https://str-leads.com/terms',
  privacy: 'https://str-leads.com/privacy',
  support: 'support@str-leads.com',
} as const;
/**
 * A planned month: three posts a week over the coming window. The calendar header counts
 * scheduled posts in the window against this target; the nudge emails use the same two numbers.
 */
export const MONTH_TARGET_POSTS = 12;
export const PLAN_WINDOW_DAYS = 30;

export const PLATFORMS = ['airbnb', 'vrbo', 'booking', 'direct'] as const;
export type Platform = (typeof PLATFORMS)[number];
export const PLATFORM_LABEL: Record<Platform, string> = {
  airbnb: 'Airbnb',
  vrbo: 'VRBO',
  booking: 'Booking.com',
  direct: 'Direct site',
};

/** `render` = a flat the host's editor made (a card, an overlay, a cover); not in the library. */
export const MEDIA_SOURCES = ['listing', 'uploaded', 'generated', 'reference', 'render'] as const;
export type MediaSource = (typeof MEDIA_SOURCES)[number];
export const MEDIA_KINDS = ['photo', 'video', 'audio'] as const;
export type MediaKind = (typeof MEDIA_KINDS)[number];

/**
 * How a generated asset was made. On a track it records the prompt and the vibe it was asked
 * for, so a post that does well can be asked later what it sounded like.
 */
export type MediaMeta = {
  music?: {
    prompt: string;
    vibe: { genre: string; tempo: string; energy: string };
    model: string;
    seconds: number;
    /** The mood the song was composed in, so a reel finds its songs by mood. */
    mood?: string;
  };
};

export const POST_STATUSES = ['draft', 'scheduled', 'published', 'failed'] as const;
export type PostStatus = (typeof POST_STATUSES)[number];

export const SCHEDULE_TIMES = ['8:00 AM', '12:00 PM', '6:30 PM'] as const;
/** The most a post's brief (the host's idea) can hold; the API refuses longer, the UI stops there. */
export const BRIEF_MAX = 4000;
export const IMPORT_STEP_MS = 900;
export const GENERATION_STEP_MS = 700;

/** Publishing providers. Each maps onto one channel a post can target. */
export const PROVIDERS = ['instagram', 'facebook', 'tiktok', 'youtube'] as const;
export type ProviderKey = (typeof PROVIDERS)[number];
export const PROVIDER_META: Record<
  ProviderKey,
  { label: string; channel: Channel; blurb: string }
> = {
  instagram: {
    label: 'Instagram',
    channel: 'IG',
    blurb: 'A Business or Creator account linked to a Facebook Page',
  },
  facebook: { label: 'Facebook Page', channel: 'FB', blurb: 'A Page you manage' },
  tiktok: { label: 'TikTok', channel: 'TT', blurb: 'Your TikTok account' },
  youtube: { label: 'YouTube Shorts', channel: 'YT', blurb: 'A channel on your Google account' },
};
export const CHANNEL_PROVIDER: Record<Channel, ProviderKey> = {
  IG: 'instagram',
  FB: 'facebook',
  TT: 'tiktok',
  YT: 'youtube',
};

export const PUBLISH_JOB_STATUSES = [
  'queued',
  'publishing',
  'pending',
  'published',
  'failed',
  'unconfirmed',
  'skipped',
] as const;
export type PublishJobStatus = (typeof PUBLISH_JOB_STATUSES)[number];
export const CONNECTION_STATUSES = ['connected', 'refresh_needed', 'disconnected'] as const;
export type ConnectionStatus = (typeof CONNECTION_STATUSES)[number];
export type ProviderMode = 'mock' | 'live';

/** Where a post's caption link goes; see posts.link_mode. */
export const LINK_MODES = ['book', 'custom', 'off'] as const;
export type LinkMode = (typeof LINK_MODES)[number];
